import {
    Configuration,
    PopupRequest,
    PublicClientApplication,
    BrowserCacheLocation,
    IPublicClientApplication,
    AuthError
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { PropsWithChildren } from 'react';
import storage from '../constants/storage';

import { getEnv } from '../helper/get-env';
import appInsights from '../analytics/app-insights';

const azureInits = async () => {
    const msalConfig: Configuration = {
        auth: {
            clientId: (await getEnv('AZURE_CLIENT_ID')) || '',
            authority: await getEnv('AZURE_AUTHORITY'), // This is a URL (e.g. https://login.microsoftonline.com/{your tenant ID})
            redirectUri: await getEnv('AZURE_REDIRECT_URI')
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage, // This configures where your cache will be stored
            storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge,
        }
    };

    // Add scopes here for ID token to be used at Microsoft identity platform endpoints.
    const loginRequest: PopupRequest = {
        scopes: [(await getEnv('AZURE_APP_SCOPE')) || '', 'user.read'],
        prompt: 'select_account'
    };

    const instance = new PublicClientApplication(msalConfig);

    const localLogin = () => localStorage.setItem(storage.AUTH_STORAGE_KEY, String(true));
    const localLogout = () => localStorage.removeItem(storage.AUTH_STORAGE_KEY);
    const checkLocalAuth = () => Boolean(localStorage.getItem(storage.AUTH_STORAGE_KEY));

    const localAuth = {
        login: localLogin,
        logout: localLogout,
        isAuth: checkLocalAuth
    };

    const logoutWithoutRedirect = async (instance: IPublicClientApplication) => {
        try {
            await instance.logoutRedirect({
                onRedirectNavigate: () => false
            });
        } catch (error) {
            const { errorCode, errorMessage, subError } = error as AuthError;

            if (errorCode !== 'interaction_in_progress') {
                throw new AuthError(errorCode, errorMessage, subError);
            }
        } finally {
            appInsights.clearAuthenticatedUserContext();
        }
    };

    const initiliazeMsalInstance = async () => await instance.initialize();

    const AzureProvider = ({ children }: PropsWithChildren) => {
        return <MsalProvider instance={instance}>{children}</MsalProvider>;
    };

    return {
        loginRequest,
        instance,
        msalConfig,
        localAuth,
        logoutWithoutRedirect,
        initiliazeMsalInstance,
        AzureProvider
    };
};

export default await azureInits();
